import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '@/lib/api';
import { useCart } from '@/contexts/CartContext';
import { useCurrency } from '@/contexts/CurrencyContext';
import { Helmet } from 'react-helmet-async';
import Header from '@/components/storefront/Header';
import Footer from '@/components/storefront/Footer';
import { CheckCircle, Clock, Truck, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

function CountdownTimer({ minutes }) {
  const endRef = useRef(Date.now() + minutes * 60 * 1000);
  const [remaining, setRemaining] = useState(minutes * 60);
  useEffect(() => {
    const interval = setInterval(() => {
      const left = Math.max(0, Math.floor((endRef.current - Date.now()) / 1000));
      setRemaining(left);
      if (left <= 0) clearInterval(interval);
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  const m = Math.floor(remaining / 60);
  const s = remaining % 60;
  return (
    <span className="inline-flex gap-1 font-mono tabular-nums">
      <span className="px-1.5 py-0.5 rounded" style={{ backgroundColor: '#000', color: '#fff', fontSize: 14 }}>{String(m).padStart(2, '0')}</span>
      <span style={{ color: '#000', fontSize: 14, fontWeight: 700 }}>:</span>
      <span className="px-1.5 py-0.5 rounded" style={{ backgroundColor: '#000', color: '#fff', fontSize: 14 }}>{String(s).padStart(2, '0')}</span>
    </span>
  );
}

export default function OrderConfirmationPage() {
  const [searchParams] = useSearchParams();
  const paymentIntent = searchParams.get('payment_intent');
  const orderId = searchParams.get('order_id');
  const sessionId = searchParams.get('session_id');
  const redirectStatus = searchParams.get('redirect_status');
  const identifier = paymentIntent || orderId || sessionId;

  const { clearCart } = useCart();
  const { formatPrice } = useCurrency();
  const [order, setOrder] = useState(null);
  const [upsells, setUpsells] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [acceptedIds, setAcceptedIds] = useState([]);
  const clearedRef = useRef(false);
  const pollCountRef = useRef(0);

  useEffect(() => {
    if (!identifier) { setLoading(false); return; }

    const pollStatus = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${identifier}`);
        if (data.status === 'paid' && data.order) {
          setPaymentStatus('paid');
          setOrder(data.order);
          setLoading(false);
          if (!clearedRef.current) { clearCart(); clearedRef.current = true; }
          api.get(`/upsell-offers/for-order/${data.order.id}`).then(u => setUpsells(u.data)).catch(() => {});
          return true;
        }
        if (data.status === 'failed') {
          setPaymentStatus('failed');
          setLoading(false);
          return true;
        }
        pollCountRef.current += 1;
        if (pollCountRef.current >= 15) { setPaymentStatus('timeout'); setLoading(false); return true; }
        return false;
      } catch {
        try {
          const { data } = await api.get(`/checkout/order-by-session/${identifier}`);
          if (data && data.payment_status === 'paid') {
            setPaymentStatus('paid'); setOrder(data); setLoading(false);
            if (!clearedRef.current) { clearCart(); clearedRef.current = true; }
            api.get(`/upsell-offers/for-order/${data.id}`).then(u => setUpsells(u.data)).catch(() => {});
            return true;
          }
        } catch {}
        pollCountRef.current += 1;
        if (pollCountRef.current >= 15) { setPaymentStatus('timeout'); setLoading(false); return true; }
        return false;
      }
    };

    let intervalId;
    const startPolling = async () => {
      const done = await pollStatus();
      if (!done) {
        intervalId = setInterval(async () => {
          const finished = await pollStatus();
          if (finished && intervalId) clearInterval(intervalId);
        }, 2000);
      }
    };
    startPolling();
    return () => { if (intervalId) clearInterval(intervalId); };
  }, [identifier, clearCart]);

  const acceptUpsell = useCallback(async (offerId) => {
    if (!order) return;
    try {
      const { data } = await api.post(`/upsell-offers/${offerId}/accept`, { order_id: order.id });
      toast.success('Added to your order!');
      setAcceptedIds(prev => [...prev, offerId]);
      setOrder(prev => prev ? { ...prev, total: data.new_total, items: [...prev.items, data.item] } : prev);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add'); }
  }, [order]);

  if (loading) return (
    <div className="min-h-screen" style={{ backgroundColor: '#FDFBF7' }}>
      <Header />
      <div className="max-w-3xl mx-auto px-6 py-20 text-center">
        <Loader2 size={32} className="animate-spin mx-auto mb-4" style={{ color: '#000' }} />
        <p className="font-sans text-sm" style={{ color: '#888' }}>Confirming your payment...</p>
      </div>
    </div>
  );

  if (['failed', 'timeout'].includes(paymentStatus)) return (
    <div className="min-h-screen" style={{ backgroundColor: '#FDFBF7' }}>
      <Header />
      <div className="max-w-3xl mx-auto px-6 py-20 text-center">
        <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: 'rgba(239,68,68,0.1)' }}>
          <AlertCircle size={32} style={{ color: '#ef4444' }} />
        </div>
        <h1 className="font-serif text-2xl mb-3" style={{ color: '#000' }}>
          {paymentStatus === 'failed' ? 'Payment Failed' : 'Payment Confirmation Pending'}
        </h1>
        <p className="font-sans text-sm mb-6" style={{ color: '#666' }}>
          {paymentStatus === 'failed' ? 'Your payment could not be processed. Please try again.' : 'Please check your email for confirmation or contact support.'}
        </p>
        <Link to="/checkout" className="inline-block px-8 py-3 font-sans text-sm font-bold uppercase tracking-wider rounded-lg" style={{ backgroundColor: '#000', color: '#fff' }}>Try Again</Link>
      </div>
      <Footer />
    </div>
  );

  if (!order) return (
    <div className="min-h-screen" style={{ backgroundColor: '#FDFBF7' }}>
      <Header />
      <div className="max-w-3xl mx-auto px-6 py-20 text-center">
        <p className="font-sans text-lg" style={{ color: '#666' }}>Order not found</p>
        <Link to="/shop" className="font-sans text-sm mt-4 inline-block" style={{ color: '#000' }}>Continue Shopping</Link>
      </div>
    </div>
  );

  return (
    <div data-testid="order-confirmation-page" className="min-h-screen" style={{ backgroundColor: '#FDFBF7' }}>
      <Helmet><title>Order Confirmed - Florashop</title></Helmet>
      <Header />
      <div className="max-w-[800px] mx-auto px-6 md:px-12 py-12">
        <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, type: 'spring' }} className="text-center mb-10">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="w-20 h-20 rounded-full mx-auto mb-5 flex items-center justify-center" style={{ backgroundColor: 'rgba(34,197,94,0.1)' }}>
            <CheckCircle size={40} strokeWidth={1.5} style={{ color: '#16a34a' }} />
          </motion.div>
          <h1 data-testid="confirmation-title" className="font-serif text-3xl md:text-4xl tracking-tight mb-3" style={{ color: '#000' }}>Thank you for your order!</h1>
          <p className="font-sans text-sm" style={{ color: '#666' }}>Order #{order.order_number} — Confirmation sent to {order.customer_email}</p>
          {order.is_test && <span className="inline-block mt-2 px-3 py-1 font-sans text-[10px] uppercase tracking-wider rounded" style={{ backgroundColor: '#ca8a04', color: '#fff' }}>Test Mode</span>}
        </motion.div>

        {upsells.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}
            data-testid="upsell-section" className="mb-10 p-6 rounded-lg border" style={{ borderColor: 'rgba(0,0,0,0.08)', backgroundColor: '#fff' }}>
            <p className="font-sans text-xs uppercase tracking-[0.15em] text-center mb-1" style={{ color: '#ca8a04' }}>Exclusive Offer — Just for you</p>
            {upsells.map(offer => {
              const accepted = acceptedIds.includes(offer.id);
              return (
                <div key={offer.id} data-testid={`upsell-card-${offer.id}`} className="text-center">
                  <div className="w-48 h-48 mx-auto rounded-lg overflow-hidden my-4" style={{ backgroundColor: '#f5f3ef' }}>
                    {offer.product?.images?.[0]?.url && <img src={offer.product.images[0].url} alt={offer.product.title} className="w-full h-full object-cover" />}
                  </div>
                  <h3 className="font-sans text-lg font-bold mb-2" style={{ color: '#000' }}>{offer.product?.title}</h3>
                  <div className="flex items-center justify-center gap-3 mb-3">
                    <span className="font-sans text-sm line-through" style={{ color: '#999' }}>{formatPrice(offer.original_price)}</span>
                    <span className="font-sans text-xl font-bold" style={{ color: '#000' }}>{formatPrice(offer.offer_price)}</span>
                    <span className="px-2 py-0.5 font-sans text-xs font-bold rounded" style={{ backgroundColor: '#000', color: '#fff' }}>SAVE {Math.round(offer.savings / offer.original_price * 100)}%</span>
                  </div>
                  <div className="flex items-center justify-center gap-2 mb-4"><Clock size={14} style={{ color: '#ca8a04' }} /><span className="font-sans text-xs" style={{ color: '#666' }}>Offer expires in </span><CountdownTimer minutes={offer.timer_minutes} /></div>
                  {accepted ? (
                    <div className="flex items-center gap-2 py-3 justify-center rounded" style={{ backgroundColor: 'rgba(34,197,94,0.06)' }}>
                      <CheckCircle size={16} style={{ color: '#16a34a' }} /><span className="font-sans text-sm font-bold" style={{ color: '#16a34a' }}>Added to Your Order!</span>
                    </div>
                  ) : (
                    <>
                      <button data-testid={`accept-upsell-${offer.id}`} onClick={() => acceptUpsell(offer.id)} className="w-full h-12 font-sans text-sm font-bold uppercase tracking-wider rounded-lg transition-all active:scale-[0.98] mb-2" style={{ backgroundColor: '#000', color: '#fff' }}>Add to My Order — {formatPrice(offer.offer_price)}</button>
                      <button className="font-sans text-xs underline" style={{ color: '#999' }}>No thanks</button>
                    </>
                  )}
                </div>
              );
            })}
          </motion.div>
        )}

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }}
          className="border rounded-lg p-6 mb-8" style={{ borderColor: 'rgba(0,0,0,0.08)', backgroundColor: '#fff' }}>
          <h2 className="font-sans text-xs uppercase tracking-[0.15em] font-bold mb-4" style={{ color: '#000' }}>Order Summary</h2>
          <div className="space-y-3 mb-4">
            {order.items?.map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-14 h-16 flex-shrink-0 overflow-hidden rounded" style={{ backgroundColor: '#f5f3ef' }}>{item.image_url && <img src={item.image_url} alt="" className="w-full h-full object-cover" />}</div>
                <div className="flex-1 min-w-0">
                  <p className="font-sans text-sm" style={{ color: '#000' }}>{item.title}{item.is_upsell && <span className="ml-1 px-1.5 py-0.5 text-[9px] uppercase rounded" style={{ backgroundColor: '#000', color: '#fff' }}>Upsell</span>}</p>
                  {item.variant_title && <p className="font-sans text-xs" style={{ color: '#888' }}>{item.variant_title}</p>}
                </div>
                <span className="font-sans text-xs" style={{ color: '#888' }}>x{item.quantity}</span>
                <span className="font-sans text-sm font-medium" style={{ color: '#000' }}>{formatPrice(item.unit_price * item.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="border-t pt-3 space-y-1.5" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
            <div className="flex justify-between font-sans text-sm"><span style={{ color: '#888' }}>Subtotal</span><span>{formatPrice(order.subtotal)}</span></div>
            {order.bundle_discount > 0 && <div className="flex justify-between font-sans text-sm"><span style={{ color: '#16a34a' }}>Bundle Savings</span><span style={{ color: '#16a34a' }}>-{formatPrice(order.bundle_discount)}</span></div>}
            {order.coupon_discount > 0 && <div className="flex justify-between font-sans text-sm"><span style={{ color: '#16a34a' }}>Discount</span><span style={{ color: '#16a34a' }}>-{formatPrice(order.coupon_discount)}</span></div>}
            <div className="flex justify-between font-sans text-sm"><span style={{ color: '#888' }}>Shipping</span><span style={{ color: '#16a34a' }}>FREE</span></div>
            <div className="flex justify-between pt-2 border-t" style={{ borderColor: 'rgba(0,0,0,0.06)' }}><span className="font-sans font-bold">Total</span><span className="font-sans text-xl font-bold">{formatPrice(order.total)}</span></div>
          </div>
          {order.shipping_address?.line1 && (
            <div className="mt-4 pt-4 border-t" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
              <p className="font-sans text-xs uppercase tracking-wider mb-1" style={{ color: '#888' }}>Shipping to</p>
              <p className="font-sans text-sm" style={{ color: '#333' }}>{order.customer_name}<br />{order.shipping_address.line1}{order.shipping_address.line2 ? `, ${order.shipping_address.line2}` : ''}<br />{order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip}</p>
            </div>
          )}
        </motion.div>

        <div className="flex flex-wrap items-center justify-center gap-4">
          <Link to={`/track?order=${order.order_number}&email=${order.customer_email}`} data-testid="track-order-link" className="font-sans text-sm font-bold uppercase tracking-wider px-8 py-3 flex items-center gap-2" style={{ backgroundColor: '#000', color: '#fff' }}><Truck size={16} /> Track Your Order</Link>
          <Link to="/shop" className="font-sans text-sm underline" style={{ color: '#666' }}>Continue Shopping</Link>
        </div>
      </div>
      <Footer />
    </div>
  );
}
